/**
 * storiesApi — full CRUD wrapper for /api/stories.
 *
 * Phase 2 (Mar 2026): create, list, view-track, delete.
 * Phase 1's `listActive()` is preserved as the canonical reader and now
 * returns inline `stories[]` per group so the viewer can flip across
 * users without re-fetching.
 */
import { api } from './api';

export const fromBackendStory = (be) => ({
  id:               be.id,
  mediaType:        be.media_type,
  mediaUrl:         be.media_url,
  thumbnailUrl:     be.thumbnail_url,
  durationSeconds:  be.duration_seconds || 5,
  createdAt:        be.created_at,
  expiresAt:        be.expires_at,
  viewersCount:    be.viewers_count || 0,
  viewedByMe:       !!be.viewed_by_me,
  authorId:         be.author_id,
  authorUsername:   be.author_username,
  authorAvatar:     be.author_avatar,
});

export const fromBackendStoryGroup = (be) => ({
  authorId:       be.author_id,
  authorUsername: be.author_username,
  authorAvatar:   be.author_avatar,
  storiesCount:   be.stories_count || (be.stories?.length || 0),
  latestAt:       be.latest_at,
  hasUnviewed:    !!be.has_unviewed,
  stories:        (be.stories || []).map(fromBackendStory),
});

export const storiesApi = {
  async listActive() {
    const res = await api.get('/stories/active');
    const items = (res?.items || []).map(fromBackendStoryGroup);
    return { items, total: res?.total ?? items.length };
  },

  async create({ mediaType, mediaUrl, thumbnailUrl, durationSeconds }) {
    const res = await api.post('/stories', {
      media_type:        mediaType,
      media_url:         mediaUrl,
      thumbnail_url:     thumbnailUrl ?? null,
      duration_seconds:  durationSeconds ?? null,
    });
    return res?.story ? fromBackendStory(res.story) : null;
  },

  async markViewed(storyId) {
    if (!storyId) return null;
    return api.post(`/stories/${encodeURIComponent(storyId)}/view`).catch(() => null);
  },

  // Iter 109: owner-only viewers list. Returns a normalised array sorted
  // newest-first (the backend stores insertion order, oldest first, so
  // we reverse here to match IG's "most recent watcher on top" UX).
  async listViewers(storyId) {
    if (!storyId) return { viewers: [], total: 0 };
    const res = await api.get(`/stories/${encodeURIComponent(storyId)}/viewers`);
    const viewers = (res?.viewers || []).map((v) => ({
      id:        v.id,
      username:  v.username || '',
      fullName:  v.full_name || '',
      avatarUrl: v.avatar_url || null,
    }));
    return { viewers: viewers.reverse(), total: res?.total ?? viewers.length };
  },

  async remove(storyId) {
    return api.delete(`/stories/${encodeURIComponent(storyId)}`);
  },
};

export default storiesApi;
